import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { trackEventMp } from "@/components/Mixpanel";
import { useEffect, useState } from "react";
import i18n from '@/i18n';

interface WelcomeSuccessModalProps {
  open: boolean;
  onClose: () => void;
  userEmail: string;
  onCustomize: () => void;
}

export const WelcomeSuccessModal = ({ 
  open, 
  onClose, 
  userEmail,
  onCustomize 
}: WelcomeSuccessModalProps) => {
  const { t } = useTranslation();
  const [modalOpenTime, setModalOpenTime] = useState<number | null>(null);
  const [isI18nReady, setIsI18nReady] = useState(i18n.isInitialized);

  useEffect(() => {
    if (!i18n.isInitialized) {
      const handleInitialized = () => setIsI18nReady(true);
      i18n.on('initialized', handleInitialized);
      return () => i18n.off('initialized', handleInitialized);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setModalOpenTime(Date.now());
      trackEventMp('welcome_modal_viewed', { email: userEmail });
    }
  }, [open, userEmail]);

  const handleCustomize = () => {
    const timeOnModal = modalOpenTime ? Date.now() - modalOpenTime : 0;
    trackEventMp('welcome_modal_cta_clicked', { 
      email: userEmail, 
      action: 'customize',
      time_on_modal: timeOnModal
    });
    onCustomize();
    onClose();
  };

  const handleDismiss = () => {
    const timeOnModal = modalOpenTime ? Date.now() - modalOpenTime : 0;
    trackEventMp('welcome_modal_dismissed', { 
      email: userEmail, 
      action: 'close',
      time_on_modal: timeOnModal
    });
    onClose();
  };

  if (!isI18nReady || !open) return null;

  return (
    <Dialog open={open} onOpenChange={handleDismiss}>
      <DialogContent className="sm:max-w-md">
        <div className="flex flex-col items-center text-center space-y-6 py-4">
          {/* Icon celebrativo */}
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-gradient-to-br from-primary to-purple-600 p-4 rounded-full">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
          </div>

          {/* Headline */}
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-foreground">
              {t('funnel.welcomeModal.modalTitle')}
            </h2>
            <p className="text-muted-foreground">
              {t('funnel.welcomeModal.modalSubtitle', { days: 14 })}
            </p>
          </div>

          {/* Benefits */}
          <div className="w-full space-y-3">
            <div className="flex items-start gap-3 text-left">
              <div className="mt-0.5 bg-green-500/10 p-1 rounded-full">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <p className="text-sm text-foreground">
                {t('funnel.welcomeModal.benefits.trial', { days: 14 })}
              </p>
            </div>
            <div className="flex items-start gap-3 text-left">
              <div className="mt-0.5 bg-green-500/10 p-1 rounded-full">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <p className="text-sm text-foreground">
                {t('funnel.welcomeModal.benefits.newsletter')}
              </p>
            </div>
            <div className="flex items-start gap-3 text-left">
              <div className="mt-0.5 bg-green-500/10 p-1 rounded-full">
                <Check className="h-4 w-4 text-green-600" />
              </div>
              <p className="text-sm text-foreground">
                {t('funnel.welcomeModal.benefits.cancel')}
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col w-full gap-2 pt-2">
            <Button 
              onClick={handleCustomize}
              size="lg"
              className="w-full"
            >
              {t('funnel.welcomeModal.ctaPrimary')}
            </Button>
            <Button 
              onClick={handleDismiss}
              variant="ghost"
              size="sm"
              className="w-full"
            >
              {t('funnel.welcomeModal.ctaSecondary')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
